// src/app/lib/types.ts
export type Logic = 'and' | 'or'
export type KendoOp =
  | 'eq'
  | 'neq'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'contains'
  | 'doesnotcontain'
  | 'startswith'
  | 'endswith'
  | 'isnull'
  | 'isnotnull'
  | 'isempty'
  | 'isnotempty'
  | 'doesnotstartswith'
  | 'doesnotendswith'
  | 'joinstar' // future support

export type AstNode = AstGroup | AstPredicate

/** Datasource & display directives injected to the root group AST */
// Intended support is so we can let the renderer ignore or ack this in the AST
// This enables externally created ASTs to omit or include this data without making the renderer handler it
// Currently supports a list for categories -- this enables multiple query output and building for diverse fields in use
export interface DataSourceDirective {
  category?: string | string[] // e.g., "process_creation" or ["process_creation","file_event"]
  platform?: string | string[] // e.g., "windows" or ["windows","linux"]
  logCategory?: string // e.g., "endpoint:sysmon" — attached by grouped renderer so prelude builders can scope by log source
  events?: string[] // override: bypasses category→events map lookup
  eventCodes?: string[] // override: bypasses category→eventCodes map lookup
  platformClauseList?: string[] // override: replaces the extra.platformClauseList from the map
  /** Routing metadata overrides — values only; field names come from toolTuningFields at render time */
  repo?: string // override: replaces the resolved table/repo name from the category map (e.g. set repo: "DeviceEvents" to override the default table)
  repoSubtype?: string // override: value for the canonical 'repo_subtype' routing field
  eventProvider?: string // override: value for the canonical 'event_provider' routing field
  selectFields?: string[] // extra columns for SQL SELECT (QRadar) or explicit column selection tools; ignored by others
}

/** Aggregation function applied to a field in a DataDisplay group-by or aggregation clause */
export type AggFn =
  | 'count'
  | 'dcount'
  | 'first'
  | 'last'
  | 'values'
  | 'sum'
  | 'avg'
  | 'min'
  | 'max'
  | 'collect'

/** A single field + optional aggregation function for use in a DataDisplay aggFields list */
export interface AggField {
  field: string // CCM field name
  fn?: AggFn // aggregation function; omit = raw field reference
  alias?: string // output alias; defaults to camelCase(fn_field) or camelCase(field)
  fromOverride?: boolean // when true, skip fieldExists check — field is tool-specific and assumed valid
}

/** Time-based bucketing or session grouping for DataDisplay */
export interface TimeBucket {
  field?: string // CCM timestamp field; tool default applied if omitted
  span?: string // e.g. '1h', '5m', '1d'; tool default applied if omitted
  type: 'bucket' | 'bin' | 'session'
}

// Data display directives -- controls output format (table, summary, aggregated, etc.)
export interface DataDisplaySpec {
  // Field projection
  fields?: string[] // explicit CCM field list (priority 1 — overrides all defaults)
  summaryFields?: string[] // used when activeView='summary' (overrides category defaults)
  detailFields?: string[] // used when activeView='detail' (overrides category defaults)
  activeView?: 'summary' | 'detail' | 'custom' // default = 'detail'
  // Category for default field set lookup; falls back to datasource.category from same directives
  category?: string | string[]
  // Aggregation
  groupBy?: string[] // CCM field names to group by
  aggFields?: (string | AggField)[] // fields + functions to aggregate; string = { field, fn: 'values' } shorthand
  timeBucket?: TimeBucket
  // Output mode
  displayMode?: 'aggregate' | 'project' // default: 'aggregate' — auto-derives groupBy+aggFields from category identityFields
  // Misc
  conditionStatement?: string // raw passthrough appended to the rendered output
  type?: string // 'standard' | 'summary' | 'detail' | 'count_time_window'
}

/** Rule-level metadata emitted as a comment block before the rendered query */
export interface metaData {
  title?: string
  mitreTechniqueIds?: string[]
  adversaryNames?: string[]
  malwareNames?: string[]
  references?: string[]
  falsepositives?: string[]
  /** Sigma rule description / auto-populated summary text */
  description?: string
  /** User-authored free-form annotation */
  comment?: string
  /** true = emit all fields including comment; false/omitted = emit nothing */
  emit?: boolean
}

// combine the directives into one object in the AST
// dataDisplay: controls output/display format (table output, summary, aggregation, etc.)
export interface RenderDirectives {
  datasource?: DataSourceDirective
  dataDisplay?: DataDisplaySpec
  metadata?: metaData
  // Future support to give instruction on what to actually render
  //render?: DirectiveToRender;
}

// Wrapper used by the orchestrator when calling renderers
export interface RenderBundle {
  filter: AstNode
  directives?: RenderDirectives
}

// Added support for group negation and group ID for the UI to be able to apply the negation
export interface AstGroup {
  kind: 'group'
  // Logical join for children; only "and" | "or" are emitted by toAst()
  logic: Logic
  children: AstNode[]
  negate?: boolean // support for group negation
  id?: string // path-based ID for debugging and UI testing
  comment?: string // optional annotation emitted before this group's logic (tool-appropriate comment syntax)
  // Define datasource values for adding event specific logic and aggregations
  // Category support: application, authentication_events, cloud, dns, file_event,
  //   firewall, image_load, network_connection, pipe_created, process_creation,
  //   proxy, Rdp_event, registry_event, security, system, webserver
  // Platform support: azure, aws, gcp, cloud, linux, macos, windows, syslog
  // Input as single object with expansion in mind instead of continually adding on the root group node
  directives?: RenderDirectives
}
export type Scalar = string | number | boolean | Date
export type ValueOrList = Scalar | Scalar[]

export interface AstPredicate {
  kind: 'pred'
  field: string
  op: KendoOp
  value?: ValueOrList
  valueType: 'string' | 'number' | 'boolean' | 'date' // boolean and date are not currently utilized but allowed for future support
}

// Array normalizer
export const toArray = <T>(v: T | T[] | undefined): T[] =>
  v == null ? [] : Array.isArray(v) ? v : [v]
